import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import clsx from "clsx";
import { TruncatedTextTooltip } from "components/common/TruncatedTextTooltip";
import { useAutoScrollToRef } from "hooks/useAutoScrollToRef";
import { STATUS_DOT_CLASS } from "../../constants";
import { QuantityDelta } from "../../QuantitiesTab/QuantityDelta";
import { KIND_TO_QUANTITY_KIND } from "../constants";
import { buildCountSegments, buildRailRowClass, sumEntryCounts } from "./utils";
import type { VisualDiffTypeRailProps } from "./types";

export const VisualDiffTypeRail = ({
  entries,
  selectedTypeKey,
  onEntrySelect,
  onShowAllSelect,
  isRemovedHintVisible,
  className,
}: VisualDiffTypeRailProps) => {
  const { t } = useTranslation();

  const { containerRef, targetRef } = useAutoScrollToRef<
    HTMLDivElement,
    HTMLButtonElement
  >({ resetDep: selectedTypeKey });

  const totalSegments = useMemo(
    () => buildCountSegments(sumEntryCounts(entries), t),
    [entries, t],
  );

  return (
    <div className={clsx("flex flex-col", className)}>
      <button
        type="button"
        onClick={onShowAllSelect}
        aria-pressed={selectedTypeKey === null}
        className={buildRailRowClass(selectedTypeKey === null)}
      >
        <span className="block text-sm font-medium text-gray-800">
          {t("smartSurveyor.planComparison.visual.showAll")}
        </span>
        {totalSegments.length > 0 && (
          <span className="block text-xs text-gray-500">
            {totalSegments.join(" · ")}
          </span>
        )}
      </button>
      <div
        ref={containerRef}
        className="mt-1 flex min-h-0 flex-1 flex-col gap-1 overflow-y-auto"
      >
        {entries.length === 0 && (
          <p className="px-2 py-1 text-sm text-gray-500">
            {t("smartSurveyor.planComparison.visual.noChangedTypes")}
          </p>
        )}
        {entries.map((entry) => (
          <button
            key={entry.key}
            ref={selectedTypeKey === entry.key ? targetRef : undefined}
            type="button"
            onClick={() => onEntrySelect(entry)}
            aria-pressed={selectedTypeKey === entry.key}
            className={buildRailRowClass(selectedTypeKey === entry.key)}
          >
            <span className="flex items-center gap-2">
              <span
                className={clsx(
                  "h-2 w-2 flex-shrink-0 rounded-full",
                  STATUS_DOT_CLASS[entry.status],
                )}
              />
              <TruncatedTextTooltip
                text={entry.name}
                className="min-w-0 flex-1 text-sm text-gray-800"
              />
              {entry.metersDelta !== null && (
                <QuantityDelta
                  delta={entry.metersDelta}
                  kind={KIND_TO_QUANTITY_KIND[entry.kind]}
                />
              )}
            </span>
            <span className="block pl-4 text-xs text-gray-500">
              {buildCountSegments(entry, t).join(" · ")}
            </span>
          </button>
        ))}
      </div>
      {isRemovedHintVisible && (
        <p className="mt-2 border-t border-gray-200 px-2 pt-2 text-xs text-gray-500">
          {t("smartSurveyor.planComparison.visual.removedHint")}
        </p>
      )}
    </div>
  );
};
